import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ApiService } from '../../abstract/api.service';
import { ResetCategories } from '../request-models/reset-categories.model';
import { ApiListResponse } from '../response-models/api-list-response.model';
import { ExecuteResponse } from '../response-models/execute-response.model';
import { Page } from '../response-models/page.model';
import { PreviewResponse } from '../response-models/preview-response.model';
import { ResetHistoryItem } from '../response-models/reset-history-item.model';
import { isRecord } from '../utils/is-record';
import { nullableBoolean } from '../utils/wire-value-utils';
import { singlePage } from '../utils/paging-utils';

/**
 * Super-admin reset of Zoho FSM, Zoho Desk and Firebase test data: `/admin/reset-integration` (hub
 * `admin-reset-integration-controller.ts`). The hub gates every route on `@Authorized('admin')` plus a
 * super-admin claim and a feature flag; a denial arrives as a 403 `ApiError`.
 */
@Injectable({ providedIn: 'root' })
export class ResetIntegrationService extends ApiService {
  constructor() {
    super('admin/reset-integration');
  }

  /** POST /admin/reset-integration/preview. The hub reads the whole body as the categories. */
  preview(categories: ResetCategories): Promise<PreviewResponse> {
    return firstValueFrom(this.request('POST', { path: 'preview' }, toPreviewResponse, categories));
  }

  /** POST /admin/reset-integration/execute. Rate limited by the hub to one per minute per user (429). */
  execute(categories: ResetCategories, confirmationPhrase: string): Promise<ExecuteResponse> {
    const body = { categories, confirmationPhrase };
    return firstValueFrom(this.request('POST', { path: 'execute' }, data => data as ExecuteResponse, body));
  }

  /**
   * GET /admin/reset-integration/history, paged with `limit`. The hub currently ignores `page` and always
   * returns the most recent `limit` resets as one page.
   */
  getHistory(page = 1, limit = 25): Promise<Page<ResetHistoryItem>> {
    const opts = { path: 'history', query: { page, limit } };
    return firstValueFrom(this.requestEnvelope('GET', opts, toHistoryPage));
  }
}

/** The hub sends `{ preview: { zohoFsm, zohoDesk, firebase, protectedRecords, ... }, isProduction }`. */
function toPreviewResponse(data: unknown): PreviewResponse {
  const source = isRecord(data) ? data : {};
  const preview = isRecord(source['preview']) ? source['preview'] : {};
  return {
    preview: {
      zohoFsm: preview['zohoFsm'] as PreviewResponse['preview']['zohoFsm'],
      zohoDesk: preview['zohoDesk'] as PreviewResponse['preview']['zohoDesk'],
      firebase: preview['firebase'] as PreviewResponse['preview']['firebase'],
    },
    protectedRecords: (preview['protectedRecords'] ?? { drones: [], profiles: [] }) as PreviewResponse['protectedRecords'],
    estimatedDurationSeconds: typeof preview['estimatedDurationSeconds'] === 'number' ? preview['estimatedDurationSeconds'] : 0,
    warnings: Array.isArray(preview['warnings']) ? preview['warnings'].filter((w): w is string => typeof w === 'string') : [],
    isProduction: nullableBoolean(source['isProduction']) ?? undefined,
  };
}

function toHistoryPage(body: ApiListResponse<unknown> | null): Page<ResetHistoryItem> {
  const items = Array.isArray(body?.data) ? body.data.filter(isRecord).map(item => item as unknown as ResetHistoryItem) : [];
  return { items, paging: body?.paging ?? singlePage(items.length) };
}
